/**
 * Shared mount/position/dismiss plumbing for the small popup palettes used
 * by `ColorTool` and `FontSizeTool` (`editorjs-tools/color/`,
 * `editorjs-tools/font-size/`). Extracted after both tools independently
 * hand-rolled the same palette logic and independently accumulated the same
 * bugs:
 *
 * - EditorJS's own inline-toolbar Popover (`.ce-popover__container`) has
 *   `overflow:hidden; max-height:270px; width:200px` in its shipped CSS —
 *   appending our palette as a DOM descendant of the toggle button (inside
 *   that popover) clips it regardless of the palette's own z-index.
 * - That same popover has ONE delegated `click` listener that re-invokes the
 *   owning tool's `surround()` for ANY click landing inside the popover
 *   item's subtree — including clicks on the palette's own swatches/chips/
 *   Confirm button, since they lived inside that same subtree. That made
 *   every palette-internal click reentrantly re-toggle the palette closed.
 *
 * The fix for both: mount the palette into `document.body` (outside the
 * popover's DOM entirely, so it can't be clipped and clicks on it don't
 * bubble into the popover's delegated listener), position it via the
 * anchor's `getBoundingClientRect()`, and own its own dismissal (outside
 * pointerdown + Escape) since it's no longer implicitly torn down by
 * EditorJS's own toolbar-close lifecycle.
 *
 * Deliberately NOT wired to `selectionchange` — reacting to it while open
 * would tear the palette down mid-pick, since every swatch/chip/reset click
 * calls `e.preventDefault()` specifically to keep the text selection alive.
 * `use-selection-toolbar.tsx` makes the same choice for the same reason.
 */

const OFFSET = 6;
/** Match `.selection-toolbar` stacking so palettes sit above EditorJS UI. */
const PALETTE_Z_INDEX = '10000';

/** Last palette opened — closed when a new one opens or via `closeActiveFloatingPalette()`. */
let activeFloatingPalette: FloatingPalette | null = null;

/** Tear down any palette left open by a prior inline-tool instance. */
export function closeActiveFloatingPalette(): void {
  activeFloatingPalette?.close();
  activeFloatingPalette = null;
}

/** Visual chrome for palette panels — use `element.style` so `position`/`z-index` from `createFloatingPalette` are preserved. */
export function applyFloatingPaletteChrome(element: HTMLElement): void {
  element.style.background = 'white';
  element.style.border = '1px solid #ddd';
  element.style.borderRadius = '6px';
  element.style.padding = '6px';
  element.style.display = 'flex';
  element.style.flexDirection = 'column';
  element.style.gap = '6px';
}

export type PaletteGestureHandlers = {
  /** Wire to the inline-tool button's `mousedown` — preserves selection and toggles the palette. */
  onToolButtonMouseDown: (e: MouseEvent) => void;
  /** Wire to EditorJS `surround()` — opens the palette unless `mousedown` already toggled it. */
  onSurroundPalette: () => void;
  /** Reset gesture flags when the tool is cleared (EditorJS inline-toolbar close). */
  resetGestureState: () => void;
};

/**
 * Coordinates palette open/close across the EditorJS inline-tool event split:
 * `mousedown` on the tool button (selection preserved via `preventDefault`) and
 * `surround()` from the popover's delegated `click` (often suppressed when
 * `mousedown` called `preventDefault`). Mirrors `useSelectionToolbar`, which
 * toggles palettes on `mousedown` directly.
 */
export function createPaletteGestureHandlers(
  getOrCreatePalette: () => FloatingPalette,
): PaletteGestureHandlers {
  let suppressNextSurroundToggle = false;

  function onToolButtonMouseDown(e: MouseEvent): void {
    e.preventDefault();
    // Keep the popover's delegated `click` from racing this gesture; we
    // already toggled the palette on `mousedown`.
    e.stopPropagation();
    getOrCreatePalette().toggle();
    suppressNextSurroundToggle = true;
    // Must outlive the trailing `click`/`surround()` — a microtask runs
    // before `click`, which would re-open or fight the toggle guard.
    window.setTimeout(() => {
      suppressNextSurroundToggle = false;
    }, 0);
  }

  function onSurroundPalette(): void {
    if (suppressNextSurroundToggle) return;
    getOrCreatePalette().open();
  }

  function resetGestureState(): void {
    suppressNextSurroundToggle = false;
  }

  return {
    onToolButtonMouseDown,
    onSurroundPalette,
    resetGestureState,
  };
}

export type FloatingPalette = {
  /** The palette's own root element — callers append their content into this. */
  element: HTMLElement;
  open: () => void;
  close: () => void;
  toggle: () => void;
  isOpen: () => boolean;
};

/**
 * Create a floating palette anchored to `anchor`. The palette is created
 * once (detached) and only appended to `document.body` for the duration it
 * is open — this matches the tools' prior "toggle = create-or-remove"
 * semantics that Confirm/outside-click/Escape all key off.
 */
export function createFloatingPalette(anchor: HTMLElement): FloatingPalette {
  const element = document.createElement('div');
  element.setAttribute('data-floating-palette', '');
  element.style.position = 'fixed';
  element.style.zIndex = PALETTE_Z_INDEX;

  let open = false;

  function isInsidePaletteOrAnchor(target: Node): boolean {
    return element.contains(target) || anchor.contains(target);
  }

  function onPointerDown(e: PointerEvent): void {
    const target = e.target as Node | null;
    if (target && isInsidePaletteOrAnchor(target)) return;
    close();
  }

  function onKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  }

  function position(): void {
    const anchorRect = anchor.getBoundingClientRect();
    // Provisional position below the anchor; measure once appended and flip
    // above if it would overflow the viewport bottom.
    element.style.top = `${anchorRect.bottom + OFFSET}px`;
    element.style.left = `${anchorRect.left}px`;
    const paletteRect = element.getBoundingClientRect();
    if (paletteRect.bottom > window.innerHeight) {
      element.style.top = `${anchorRect.top - paletteRect.height - OFFSET}px`;
    }
    if (paletteRect.right > window.innerWidth) {
      element.style.left = `${Math.max(0, window.innerWidth - paletteRect.width - OFFSET)}px`;
    }
  }

  function doOpen(): void {
    if (open) return;
    closeActiveFloatingPalette();
    open = true;
    activeFloatingPalette = api;
    document.body.appendChild(element);
    position();
    // Defer dismissal listeners so the opening pointer/mouse gesture cannot
    // immediately hit the capture-phase outside handler.
    window.setTimeout(() => {
      if (!open) return;
      document.addEventListener('pointerdown', onPointerDown, true);
      document.addEventListener('keydown', onKeyDown, true);
    }, 0);
  }

  function close(): void {
    if (!open) return;
    open = false;
    document.removeEventListener('pointerdown', onPointerDown, true);
    document.removeEventListener('keydown', onKeyDown, true);
    element.remove();
    if (activeFloatingPalette === api) {
      activeFloatingPalette = null;
    }
  }

  function toggle(): void {
    if (open) close();
    else doOpen();
  }

  const api: FloatingPalette = {
    element,
    open: doOpen,
    close,
    toggle,
    isOpen: () => open,
  };

  return api;
}
